import Big from "big.js";
import { z } from "zod";
import config from "./decimal-config.json";

export type DecimalValue = string | number;
export const DECIMAL_CONFIG = config;
export const DECIMAL_ZERO = config.zero;
const Decimal = Big();
Decimal.DP = config.divisionDecimalPlaces;
const DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

function validDecimalSyntax(value: DecimalValue): boolean {
  return DECIMAL_PATTERN.test(String(value).trim());
}

export const computedDecimalSchema = z.union([z.string(), z.number().finite()])
  .refine(validDecimalSyntax, config.invalid);

function validDecimal(value: DecimalValue): boolean {
  const text = String(value).trim();
  if (!validDecimalSyntax(value)) return false;
  const [mantissa, exponentText] = text.toLowerCase().split("e");
  const exponent = Number(exponentText ?? 0);
  const fractionDigits = (mantissa.split(".")[1]?.length ?? 0) - exponent;
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > config.maximumIntegerDigits + config.maximumFractionDigits) return false;
  return fractionDigits <= config.maximumFractionDigits && decimal(value).e < config.maximumIntegerDigits;
}

function trimDecimal(value: DecimalValue): string {
  return String(value).trim();
}

function validateDecimal(value: DecimalValue, context: z.RefinementCtx): void {
  if (validDecimal(value)) return;
  context.addIssue({ code: z.ZodIssueCode.custom, message: config.invalid, fatal: true });
}

export const decimalSchema = z.union([z.string(), z.number().finite()])
  .superRefine(validateDecimal).transform(trimDecimal);

function emptyDecimalToNull(): null {
  return null;
}

export const nullableDecimalSchema = z.union([
  z.string().trim().length(0).transform(emptyDecimalToNull), decimalSchema, z.null(),
]);

export function decimalText(value: DecimalValue): string {
  return decimalSchema.parse(value);
}

export function computedDecimalText(value: DecimalValue): string {
  return String(computedDecimalSchema.parse(value)).trim();
}

function decimal(value: DecimalValue): Big {
  return new Decimal(String(value).trim().replace(/^\+/, ""));
}

export function decimalCompare(left: DecimalValue, right: DecimalValue): number {
  return decimal(left).cmp(decimal(right));
}

export function decimalEqual(left: DecimalValue, right: DecimalValue): boolean {
  return decimalCompare(left, right) === 0;
}

export function decimalAdd(left: DecimalValue, right: DecimalValue): string {
  return decimal(left).plus(decimal(right)).toFixed();
}

export function decimalSubtract(left: DecimalValue, right: DecimalValue): string {
  return decimal(left).minus(decimal(right)).toFixed();
}

export function decimalMultiply(left: DecimalValue, right: DecimalValue): string {
  return decimal(left).times(decimal(right)).toFixed();
}

export function decimalDivide(left: DecimalValue, right: DecimalValue): string {
  const numerator = decimal(left);
  const denominator = decimal(right);
  const Division = Big();
  Division.DP = Math.max(config.divisionDecimalPlaces,
    numerator.c.length - numerator.e + denominator.c.length + Math.max(0, denominator.e));
  return new Division(numerator).div(denominator).toFixed();
}

export function decimalSum(values: readonly DecimalValue[]): string {
  return values.reduce<string>(decimalAdd, DECIMAL_ZERO);
}

export function decimalNumber(value: DecimalValue): number {
  return decimal(value).toNumber();
}

export function decimalRound(value: DecimalValue, places: number): string {
  return decimal(value).toFixed(places, Big.roundHalfUp);
}

export function decimalPlainText(value: DecimalValue): string {
  const text = String(computedDecimalSchema.parse(value)).trim();
  return /e/i.test(text) ? decimal(text).toFixed() : text;
}

function positiveDecimal(value: string): boolean {
  return validDecimal(value) && decimalCompare(value, 0) > 0;
}

function nonnegativeDecimal(value: string): boolean {
  return validDecimal(value) && decimalCompare(value, 0) >= 0;
}

export const positiveDecimalSchema = decimalSchema.refine(positiveDecimal, config.positive);
export const nonnegativeDecimalSchema = decimalSchema.refine(nonnegativeDecimal, config.nonnegative);
